import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { archiveCategories } from '@/mocks/portfolio';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import SlotText from '@/components/feature/SlotText';
import { isTextCover } from '@/components/feature/ImageViewer';

export default function Archive() {
  const [searchParams] = useSearchParams();
  // Always default to 'all' on first load; URL param only overrides on navigation from Selected Works
  const [active, setActive] = useState<string>(searchParams.get('cap') || 'all');
  const sectionRef = useRef<HTMLElement>(null);
  const { ref, revealed } = useScrollReveal();

  const totalCount = new Set(archiveCategories.flatMap((c) => c.items.map((it) => it.id))).size;

  // Restore scroll position when returning from case detail
  useEffect(() => {
    const savedY = sessionStorage.getItem('worksScrollY');
    if (savedY) {
      const y = parseInt(savedY, 10);
      // Delay slightly to ensure DOM is ready after page transition
      const t = window.setTimeout(() => {
        window.scrollTo(0, y);
        sessionStorage.removeItem('worksScrollY');
      }, 50);
      return () => window.clearTimeout(t);
    }
  }, []);

  // Sync active from URL — e.g. clicking a focus label in Selected Works
  useEffect(() => {
    const cap = searchParams.get('cap');
    if (cap && cap !== active) {
      setActive(cap);
      const t = window.setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return () => window.clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const visible =
    active === 'all' ? archiveCategories : archiveCategories.filter((c) => c.key === active);

  const tabs = [
    { key: 'all', label: 'All', ja: 'すべて' },
    ...archiveCategories.map((c) => ({ key: c.key, label: c.label, ja: c.ja })),
  ];

  const shortCat = (code: string) =>
    ({
      'brand-cx': 'Brand / CX',
      'digital-service': 'Digital / Service',
      'experience-space': 'Experience / Space',
      'retail-communication': 'Retail / Comm',
      'creative-movie': 'Creative / Movie',
      // legacy capability-based keys (kept for any existing URLs)
      'business-design': 'Business Design',
      'cx-planning': 'CX Planning',
      'brand-strategy': 'Brand Strategy',
      'communication-strategy': 'Communication',
    })[code] || code;

  const handleCardClick = () => {
    sessionStorage.setItem('worksScrollY', String(window.scrollY));
  };

  const renderCard = (it: { id: string; title: string; year: string; category: string; meta: string; cover: string }) => {
    const cardBody = (
      <div className="group cursor-pointer">
        <div className="relative overflow-hidden aspect-[4/3] mb-3">
            {it.cover ? (
              <img
                src={it.cover}
                alt={it.title}
                className={`w-full h-full transition-transform duration-700 ease-out group-hover:scale-110 ${
                  it.id === 'regional-currency-20'
                    ? 'object-contain object-center bg-background-50'
                    : it.id === 'aizu-coin'
                    ? 'object-contain object-center bg-background-50'
                    : it.id === 'aizu-coin-design-guideline'
                    ? 'object-cover object-[15%_top]'
                    : it.id === 'q-sai-aojiru'
                    ? 'object-cover object-[15%_20%]'
                    : isTextCover(it.id)
                    ? 'object-contain object-center bg-background-50'
                    : 'object-cover object-top'
                }`}
              />
            ) : (
              <div className="w-full h-full bg-background-200 flex flex-col items-center justify-center gap-2 text-foreground-400">
                <i className="ri-image-line w-8 h-8 flex items-center justify-center text-2xl" />
                <span className="font-label text-[10px] tracking-[0.12em] uppercase">No Image</span>
              </div>
            )}
            <div className="flow-glow absolute inset-0" />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400">
              {it.year}
            </span>
            <span className="w-1 h-px bg-foreground-300" />
            <span className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400">
              {shortCat(it.category)}
            </span>
          </div>
          <h3 className="font-heading text-xs md:text-sm text-foreground-950 leading-snug tracking-[-0.02em]">
            <SlotText text={it.title} scrambleColor="rainbow" speed="fast" />
          </h3>
          <p className="text-xs text-foreground-500 leading-relaxed">{it.meta}</p>
        </div>
      </div>
    );

    return it.id ? (
      <Link key={it.id} to={`/case/${it.id}`} className="block" onClick={handleCardClick}>
        {cardBody}
      </Link>
    ) : (
      <div key={it.title} className="block">
        {cardBody}
      </div>
    );
  };

  return (
    <section id="archive" ref={sectionRef} className="py-20 md:py-28 bg-background-50">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6">
        {/* Header */}
        <div
          ref={ref}
          className={`mb-12 transition-all duration-1000 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            <span className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              All Works — {totalCount} Projects
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-6">
              <h2 className="font-heading fw-bold text-[clamp(32px,4.5vw,60px)] text-foreground-950 leading-[0.93] tracking-[-0.04em]">
                Breadth
                <br />
                <SlotText text="& Depth." as="span" className="fw-medium marker-yellow" color="oklch(0.09 0.01 70)" scrambleColor="rainbow" />
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-foreground-600 text-base leading-relaxed">
                今までの広告・販促として切り離さず、事業設計からブランド・顧客体験・コミュニケーションまで、一貫した視点で実装してきた経験の幅と深さです。「広さ」と「深さ」を併せ持つからこそ、課題の上流から顧客接点の末端まで一貫してリードできます。
              </p>
            </div>
          </div>
        </div>

        {/* Category tabs — attention-strong, clear click affordance */}
        <div
          className={`mb-12 transition-all duration-1000 delay-150 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="font-label text-[9px] tracking-[0.14em] uppercase text-foreground-400 mb-2">
            ↓ カテゴリで絞り込む &nbsp;·&nbsp; Click to filter by category
          </p>
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`group flex items-center gap-2 px-5 py-3 cursor-pointer transition-all whitespace-nowrap border-2 ${
                  active === t.key
                    ? 'bg-foreground-950 border-foreground-950 text-background-50'
                    : 'bg-background-50 border-foreground-950/20 text-foreground-700 hover:border-primary-500 hover:text-foreground-950'
                }`}
              >
                {active !== t.key && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 group-hover:animate-pulse flex-shrink-0" />
                )}
                <SlotText
                  text={t.label}
                  as="span"
                  className={`font-label text-[11px] tracking-[0.12em] uppercase ${
                    active === t.key ? 'text-background-50' : 'text-foreground-700 group-hover:text-foreground-950 transition-colors'
                  }`}
                />
                <span className={`font-label text-[9px] tracking-[0.08em] ${
                  active === t.key ? 'text-background-50/60' : 'text-foreground-500'
                }`}>
                  {t.ja}
                </span>
                {active === t.key && (
                  <i className="ri-check-line w-3 h-3 flex items-center justify-center text-primary-400" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Category sections — stacked when showing all */}
        <div
          className={`transition-all duration-1000 delay-300 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {visible.map((cat, idx) => (
            <div key={cat.key} className={`${idx > 0 ? 'pt-14' : ''}`}>
              {/* Category heading */}
              <div className="flex items-end justify-between gap-4 border-t-2 border-foreground-950 pt-4 mb-2">
                <div>
                  <h3 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-[0.95] tracking-[-0.03em]">
                    <SlotText text={cat.label} as="span" />
                  </h3>
                  <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 mt-1">
                    {cat.ja}
                  </p>
                </div>
                <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 whitespace-nowrap">
                  {cat.items.length} projects
                </p>
              </div>
              <p className="font-label text-[10px] tracking-[0.1em] uppercase text-foreground-500 mb-6">
                {cat.caption}
              </p>

              {/* Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {cat.items.map(renderCard)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}